import React from 'react';
import { useNavigate } from 'react-router-dom';
import { trackClick } from '../utils/analytics';
import {
  MinimalGlobalStyle,
  Container,
  Inner,
  BackButton,
  Header,
  HeroRow,
  Avatar,
  HeroText,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
  Card,
  CardTop,
  CardTitle,
  Badge,
  CardDescription,
  CardLinks,
  CardLink,
  Tag,
  TagRow,
  ProjectCard,
  StatsGrid,
  StatCell,
  StatValue,
  StatLabel,
  Paragraph,
  FindingsList,
  Finding,
} from '../components/minimal';
import { vulnerabilities, consciousnessPaper, researchStats } from '../data/research';

const ResearchPage: React.FC = () => {
  const navigate = useNavigate();

  const handleLinkClick = (item: string, label: string) => {
    trackClick('research_link', { item, link_type: label });
  };

  return (
    <>
      <MinimalGlobalStyle />
      <Container>
        <BackButton />
        <Inner>
          <Header>
            <HeroRow>
              <Avatar>
                <img src="/profile.jpg" alt="Thomas Ryan" />
              </Avatar>
              <HeroText>
                <PageTitle>Security Research</PageTitle>
                <Bio>
                  Independent security researcher focused on kernel, browser, and OS attack surfaces.
                  I find bugs in production systems through source-level auditing, fuzzing, and
                  architecture analysis.
                </Bio>
              </HeroText>
            </HeroRow>
            <HeaderLinks>
              <HeaderLink as="button" onClick={() => { trackClick('research_to_build'); navigate('/build'); }}>
                &rarr; Build
              </HeaderLink>
              <HeaderLink href="https://github.com/Atomics-hub" target="_blank" rel="noopener noreferrer">GitHub</HeaderLink>
              <HeaderLink href="https://issues.chromium.org/issues/489577364" target="_blank" rel="noopener noreferrer">Chromium</HeaderLink>
            </HeaderLinks>
          </Header>

          <StatsGrid>
            <StatCell>
              <StatValue>{researchStats.bugsFound}</StatValue>
              <StatLabel>Bugs found</StatLabel>
            </StatCell>
            <StatCell>
              <StatValue>{researchStats.targetsPatched}</StatValue>
              <StatLabel>Targets patched</StatLabel>
            </StatCell>
            <StatCell>
              <StatValue>{researchStats.attackSurfaces}</StatValue>
              <StatLabel>Attack surfaces</StatLabel>
            </StatCell>
          </StatsGrid>

          <Section>
            <SectionTitle>Vulnerability Research</SectionTitle>
            {vulnerabilities.map(item => (
              <ProjectCard key={item.title} project={item} onLinkClick={handleLinkClick} />
            ))}
          </Section>

          <Section>
            <SectionTitle>Published Papers</SectionTitle>
            <Card>
              <CardTop>
                <CardTitle>{consciousnessPaper.title}</CardTitle>
                <Badge $variant="published">PUBLISHED</Badge>
              </CardTop>
              <CardDescription>{consciousnessPaper.meta}</CardDescription>
              <Paragraph>{consciousnessPaper.abstract}</Paragraph>
              <FindingsList>
                {consciousnessPaper.findings.map(f => <Finding key={f}>{f}</Finding>)}
              </FindingsList>
              <TagRow>
                {consciousnessPaper.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
              </TagRow>
              <CardLinks>
                {consciousnessPaper.links.map(link => (
                  <CardLink
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleLinkClick('consciousness-paper', link.label)}
                  >
                    {link.label} &rarr;
                  </CardLink>
                ))}
              </CardLinks>
            </Card>
          </Section>
        </Inner>
      </Container>
    </>
  );
};

export default ResearchPage;
